/**
 * OrderedFlowEngine — the live game's logical core, Phaser-free.
 *
 * The game is an ordered flow of events over one logical state. A dispatch
 * resolves an attacker's ENTIRE lap the moment it happens; the belt animation
 * that follows is a replay of a decision already made. Several attackers ride
 * at once, but that is pacing: outcomes are serial, in event order.
 *
 * The normative rules live in **`docs/ordered-flow-semantics.md`** — read that
 * first. In one paragraph: `dispatch` is legal only when the in-flight ring has
 * room for the attacker's slotCount (a full belt REFUSES the tap — the classic
 * rule; bursting is retired); it pops the attacker, resolves its lap through
 * the shared `AtomicSimulator.resolveLap`, ages every rider still in the ring
 * by one action, and either culls the attacker (ammo spent) or appends it to
 * the ring at age 0. `settle` arrives the ring's oldest rider and ages the
 * rest. In MODEL mode a front rider whose age reaches `conveyorCap` is
 * force-arrived before the next player action (the age bound — "up to five
 * time steps of grace"). An arrival that does not fit the stack is the loss;
 * the board reaching zero cubes is the win, immediately.
 *
 * TWO MODES, one rulebook:
 *
 *   - `model` (default) — the full model, age bound enforced. What the solver,
 *     the binding tests and offline witness verification run.
 *   - `live` — what `GameScene` runs. The belt is real-time: a rider leaves it
 *     ONLY at lap end (the clock's settle), so the age bound's forced arrival
 *     never fires — evicting a mid-lap rider is precisely the mechanic Arthur
 *     rejected. Ages are still tracked (identically), just never enforced.
 *     Everything else — room-gated dispatch, arrival = the loss, immediate
 *     win — is byte-identical. The model's defer window is strictly *within*
 *     live reality (a lap outlasts `conveyorCap` prompt taps), so every model
 *     trace is executable live by pacing; see the semantics doc's soundness
 *     section.
 *
 * The engine and `AtomicSimulator.applyDispatch` implement the same rules and
 * are pinned together by `__tests__/ordered-flow-binding.test.ts`; they share
 * `resolveLap` so a lap kills cubes by literally the same code.
 *
 * State representation is `SolverState` with `conveyor` read as the in-flight
 * ring; riders carry `age` (see `SolverAttacker.age`), and `hashState` covers
 * both the ring's order and each rider's age.
 */

import {
  applyHammerTriggers,
  isWin,
  resolveLap,
  stackSlotsUsed,
  type HammerTriggerRecord,
  type LapShotRecord,
} from './PerfectSolver/AtomicSimulator';
import {
  attackerHash,
  buildSolverState,
  canonicalizeStack,
  getQueueLaneHash,
  hashState,
  type SolverAttacker,
  type SolverState,
} from './PerfectSolver/SolverState';
import type { AttackerData } from '../entities/Attacker';
import type {
  BarricadeDef,
  BlockadeWallDef,
  CellStack,
  LockedDoorDef,
  MegaBlockDef,
} from '../types/board';

export type { LapShotRecord, HammerTriggerRecord };

// ── Building the logical state from the running game ───────────────────────

/**
 * What `GameScene` hands the engine: the objects it ALREADY built, not a second
 * reading of the level file.
 *
 * This matters. Between an `ImageLevel` on disk and the board a player sees sit
 * `padGrid` (centres a small board inside 42×42, shifting every row/col index),
 * `LevelGenerator.fromGrid`'s single-colour safeguard (recolours cells), and
 * `autoPickColors` (assigns door/wall colours from a seeded RNG). Rebuilding the
 * model from the file would reproduce none of those, and every one of them would
 * be a silent divergence in cell coordinates or colour. So the model is built
 * from the runtime's own grid, defs and attacker list, and the shot coordinates
 * it produces address the very cells `BoardSystem` holds.
 */
export interface FlowStateInput {
  /** The board as `BoardSystem` received it (padded, post-safeguard). */
  grid: CellStack[][];
  /** Snake cells keyed by `cellKey` (row * 256 + col) — what `LevelData` carries. */
  snakeCells?: ReadonlySet<number>;
  megaBlockDefs?: MegaBlockDef[];
  lockedDoorDefs?: LockedDoorDef[];
  blockadeWallDefs?: BlockadeWallDef[];
  barricadeDefs?: BarricadeDef[];
  /** Attackers in queue order, hammers already spliced in, runtime ids assigned. */
  attackers: readonly AttackerData[];
  numQueues: number;
  stackSlots: number;
  conveyorCap: number;
}

/** `cellKey`'s stride — see `src/types/board.ts`. */
const CELL_KEY_STRIDE = 256;

/**
 * Build the engine's logical state from the live game's own systems.
 *
 * Attacker ids are the RUNTIME ids, so the engine and the scene speak the same
 * identity with no mapping layer. (Witness replay still maps solver ids onto
 * runtime ids — see `buildSolverIdMap` — because a witness names solver ids.)
 */
export function buildFlowState(input: FlowStateInput): SolverState {
  const boardWidth = input.grid[0]?.length ?? 0;

  const snakeCells: number[] = [];
  for (const key of input.snakeCells ?? []) {
    const row = Math.floor(key / CELL_KEY_STRIDE);
    const col = key - row * CELL_KEY_STRIDE;
    snakeCells.push(row * boardWidth + col);
  }

  const attackers: SolverAttacker[] = input.attackers.map(a => {
    const out: SolverAttacker = {
      id: a.id,
      orbs: a.orbs.map(o => ({ color: o.colorIndex, ammo: o.ammo })),
      slotCount: a.orbs.length,
    };
    if ((a.frozen ?? 0) > 0) out.frozen = a.frozen;
    if (a.isHammer) out.isHammer = true;
    if (a.orbs.some(o => o.mystery)) out.mystery = true;
    return out;
  });

  return buildSolverState({
    cellGrid: input.grid,
    attackers,
    snakeCells,
    megaBlocks: input.megaBlockDefs,
    lockedDoors: input.lockedDoorDefs,
    blockadeWalls: input.blockadeWallDefs,
    barricades: input.barricadeDefs,
    numQueues: input.numQueues,
    stackSlots: input.stackSlots,
    conveyorCap: input.conveyorCap,
  });
}

// ── Events ─────────────────────────────────────────────────────────────────

/**
 * The model's action alphabet. A trace is a list of these; the live game, the
 * autoplay bot and the witness driver all produce nothing else.
 *
 * `settle` is NOT the retired `advance`: `advance` moved a survivor off a
 * phantom post-lap holding pen the game never had, whereas `settle` is the
 * belt's oldest rider finishing the lap it was always going to finish.
 */
export type FlowEvent =
  | { kind: 'dispatch'; attackerId: number }
  | { kind: 'settle' };

/**
 * Why an attacker left the in-flight ring for the stack.
 *
 *   `settle` — the oldest rider's lap ended (live: the clock; model: the
 *              player's "I waited").
 *   `forced` — the age bound: the front rider's age reached `conveyorCap`, so
 *              its arrival preceded the next player action. Model mode only —
 *              the live belt is real-time and a rider leaves it only at lap end.
 *   `drain`  — quiescence: no dispatch is legal and the ring empties FIFO.
 */
export type ArrivalCause = 'settle' | 'forced' | 'drain';

/** One in-flight → stack transition. */
export interface ArrivalRecord {
  attackerId: number;
  cause: ArrivalCause;
  /** First stack slot it occupies; it spans `slotCount` from here. */
  stackSlot: number;
  /** True when it did not fit — this arrival loses the game. */
  overflow: boolean;
  /**
   * True when the rider was removed instead of seated because every orb was
   * empty. Exhausted riders never arrive anywhere — `dispatch` culls them at
   * resolve time, so this firing at all means an invariant upstream broke (it
   * is accompanied by a loud console.error). `stackSlot` is -1 and `overflow`
   * false on a culled arrival.
   */
  culled?: boolean;
}

/** Everything one `dispatch` did, in the order it did it. */
export interface DispatchOutcome {
  attackerId: number;
  source: 'queue' | 'stack';
  /**
   * Age-bound arrivals that preceded this dispatch (model mode only; the live
   * game never produces one — its riders leave the belt only at lap end).
   */
  forcedArrivals: ArrivalRecord[];
  /**
   * Shots taken this lap in perimeter order — the animation script. Empty when
   * the attacker found nothing to shoot (a legal, common, wasted lap).
   */
  shots: LapShotRecord[];
  /** Per-orb ammo after the lap. */
  postOrbs: { color: number; ammo: number }[];
  /** True when every orb emptied: the attacker is gone and frozen counters ticked. */
  depleted: boolean;
  /** Queue attacker ids whose `frozen` counter reached 0 on this dispatch. */
  thawed: number[];
  /** Hammers that auto-triggered off queue fronts after the pop. */
  hammers: HammerTriggerRecord[];
  /** True when the attacker joined the in-flight ring (i.e. `!depleted`). */
  inFlight: boolean;
  /** Board reached zero cubes on this dispatch. */
  won: boolean;
  /** Game ended in a loss on this dispatch (an overflowing forced arrival). */
  lost: boolean;
}

/**
 * Why the engine refused an event. `no-belt-room` is the one EXPECTED refusal —
 * it is the classic "tap while the belt is full" and the UI answers it with the
 * gentle conveyor flash, not an error. Every other member is a hard error
 * upstream.
 */
export type FlowRefusal =
  | 'game-over'
  | 'attacker-not-found'
  | 'is-hammer'
  | 'frozen'
  | 'no-consensus'
  | 'too-wide-for-belt'
  | 'no-belt-room'
  | 'ring-empty';

/** Engine construction options. */
export interface FlowEngineOptions {
  /**
   * `model` (default): the full rulebook, age bound enforced — the solver's
   * twin. `live`: the shipping game — the belt is clock-driven, a rider leaves
   * it only at lap end, so the age bound's forced arrival never fires (ages
   * are tracked identically, never enforced). See the module header.
   */
  mode?: 'model' | 'live';
}

export type FlowStatus = 'running' | 'won' | 'lost';

// ── Engine ─────────────────────────────────────────────────────────────────

export class OrderedFlowEngine {
  private _state: SolverState;
  private _hash: bigint;
  private _status: FlowStatus = 'running';
  private _lastRefusal: FlowRefusal | null = null;
  /** True when the age bound's forced arrivals fire (model mode). */
  private readonly _enforceAgeBound: boolean;
  /** Every event applied, in order. The auditable trace. */
  private readonly _trace: FlowEvent[] = [];

  constructor(state: SolverState, options: FlowEngineOptions = {}) {
    this._state = state;
    this._hash = hashState(state);
    this._enforceAgeBound = (options.mode ?? 'model') === 'model';
    if (isWin(state)) this._status = 'won';
  }

  get state(): SolverState { return this._state; }
  get hash(): bigint { return this._hash; }
  get status(): FlowStatus { return this._status; }
  get won(): boolean { return this._status === 'won'; }
  get lost(): boolean { return this._status === 'lost'; }
  get over(): boolean { return this._status !== 'running'; }
  /** Why the last event was refused, or null if it was accepted. */
  get lastRefusal(): FlowRefusal | null { return this._lastRefusal; }
  /** The event trace so far — a replayable, auditable history. */
  get trace(): readonly FlowEvent[] { return this._trace; }

  /** Cubes still on the board (mega HP counts as one cube per point). */
  cubesRemaining(): number {
    let n = 0;
    for (let i = 0; i < this._state.board.length; i++) {
      if (this._state.board[i] !== 0) n++;
    }
    return n;
  }

  /** In-flight ring, oldest first. */
  inFlightIds(): number[] {
    return this._state.conveyor.map(a => a.id);
  }

  /** Slots the in-flight ring is using. */
  inFlightSlotsUsed(): number {
    let n = 0;
    for (const a of this._state.conveyor) n += a.slotCount;
    return n;
  }

  /** Slots the stack is using. */
  stackSlotsUsed(): number {
    return stackSlotsUsed(this._state);
  }

  /**
   * Stack slot layout as the UI sees it: one entry per slot, repeating a
   * multi-slot attacker's id across its slots, `null` where empty.
   */
  stackSlotIds(): (number | null)[] {
    const slots: (number | null)[] = Array.from({ length: this._state.stackCap }, () => null);
    let idx = 0;
    for (const a of this._state.stack) {
      for (let k = 0; k < a.slotCount && idx < slots.length; k++) slots[idx++] = a.id;
    }
    return slots;
  }

  /** Look an attacker up wherever it currently lives. */
  find(attackerId: number): { attacker: SolverAttacker; where: 'queue' | 'in-flight' | 'stack' } | null {
    for (const lane of this._state.queueLanes) {
      for (const a of lane) {
        if (a.id === attackerId) return { attacker: a, where: 'queue' };
      }
    }
    for (const a of this._state.conveyor) {
      if (a.id === attackerId) return { attacker: a, where: 'in-flight' };
    }
    for (const a of this._state.stack) {
      if (a.id === attackerId) return { attacker: a, where: 'stack' };
    }
    return null;
  }

  /**
   * Would `dispatch(attackerId)` be accepted right now? Pure — no side effects,
   * no forced arrivals. Used for enumeration and for pre-tap UI feedback; the
   * real answer is whatever {@link dispatch} returns.
   */
  canDispatch(attackerId: number): boolean {
    return this.checkDispatch(attackerId) === null;
  }

  /**
   * Why `dispatch(attackerId)` would be refused right now, or null when it is
   * legal. Pure — no side effects. Lets a caller tell the gentle
   * `no-belt-room` refusal (wait for a lap to end) from a genuine illegality
   * without performing the dispatch.
   */
  dispatchRefusal(attackerId: number): FlowRefusal | null {
    return this.checkDispatch(attackerId);
  }

  /** Every dispatch legal from here, in queue-lane order then stack order. */
  legalDispatchIds(): number[] {
    if (this.over) return [];
    const out: number[] = [];
    const seen = new Set<number>();
    for (const lane of this._state.queueLanes) {
      const front = lane[0];
      if (!front || seen.has(front.id)) continue;
      if (this.checkDispatch(front.id) !== null) continue;
      seen.add(front.id);
      out.push(front.id);
    }
    for (const a of this._state.stack) {
      if (seen.has(a.id)) continue;
      if (this.checkDispatch(a.id) !== null) continue;
      seen.add(a.id);
      out.push(a.id);
    }
    return out;
  }

  /** Every event legal from here — dispatches plus `settle` when the ring is loaded. */
  legalEvents(): FlowEvent[] {
    const events: FlowEvent[] = this.legalDispatchIds().map(id => ({ kind: 'dispatch' as const, attackerId: id }));
    if (!this.over && this._state.conveyor.length > 0) events.push({ kind: 'settle' });
    return events;
  }

  // ── Events ───────────────────────────────────────────────────────────────

  /** Apply one event. Returns false (and sets {@link lastRefusal}) if refused. */
  apply(event: FlowEvent): boolean {
    if (event.kind === 'settle') return this.settle() !== null;
    return this.dispatch(event.attackerId) !== null;
  }

  /**
   * DISPATCH — §1 of the semantics doc. Fires the front rider's age-bound
   * arrival if one is due (model mode), pops the attacker, resolves its whole
   * lap, ages the ring, then either culls it or appends it to the ring at age
   * 0. Returns null when the event is illegal (see {@link lastRefusal}) — a
   * `no-belt-room` refusal is the classic full-belt tap, expected and gentle.
   */
  dispatch(attackerId: number): DispatchOutcome | null {
    this._lastRefusal = null;
    const refusal = this.checkDispatch(attackerId);
    if (refusal !== null) {
      this._lastRefusal = refusal;
      return null;
    }

    const located = this.locateDispatchable(attackerId)!;
    const { attacker, source } = located;

    const outcome: DispatchOutcome = {
      attackerId,
      source,
      forcedArrivals: [],
      shots: [],
      postOrbs: [],
      depleted: false,
      thawed: [],
      hammers: [],
      inFlight: false,
      won: false,
      lost: false,
    };

    // 1. Age bound (model mode). A front rider that has used its whole grace
    //    window (`age >= conveyorCap`) is force-arrived before this action.
    //    That arrival can lose the game, and the player is allowed to lose this
    //    way. Never fires in live mode: the live belt is real-time and a rider
    //    leaves it only at lap end. Ages strictly decrease front-to-back, so at
    //    most one rider can be at the bound — the loop is a guard.
    if (this._enforceAgeBound) {
      while (this.frontAtAgeBound()) {
        const arrival = this.arriveOldest('forced');
        /* istanbul ignore next — frontAtAgeBound implies a non-empty ring */
        if (arrival === null) break;
        outcome.forcedArrivals.push(arrival);
        if (arrival.overflow) {
          this._status = 'lost';
          outcome.lost = true;
          this._trace.push({ kind: 'dispatch', attackerId });
          return outcome;
        }
      }
    }

    // 2. Remove from source.
    if (source === 'queue') this.removeFromQueue(attacker);
    else this.removeFromStack(attacker);

    // 3. Resolve the lap through the SHARED simulator.
    const lap = resolveLap(this._state, this._hash, attacker, undefined, outcome.shots);
    this._state = lap.state;
    this._hash = lap.hash;

    // 3b. This was a player action: every rider already in the ring ages by 1.
    //     Before the survivor lands, so the dispatched attacker ends at age 0.
    this.ageRing();

    const post = lap.postAttacker;
    outcome.postOrbs = post.orbs.map(o => ({ color: o.color, ammo: o.ammo }));
    outcome.depleted = post.orbs.every(o => o.ammo <= 0);

    // 4. Land it.
    if (outcome.depleted) {
      outcome.thawed = this.tickFrozenQueue();
    } else {
      // A dispatched attacker is never still frozen (the force-dispatch escape
      // hatch clears it on the way out, like `QueueSystem.forceDispatchFrom`).
      const rider: SolverAttacker = { id: post.id, orbs: post.orbs, slotCount: post.slotCount };
      if (post.isHammer) rider.isHammer = post.isHammer;
      if (post.mystery) rider.mystery = post.mystery;
      this._state = { ...this._state, conveyor: [...this._state.conveyor, rider] };
      this._hash = hashState(this._state);
      outcome.inFlight = true;
    }

    // 5. Hammers exposed by the pop auto-trigger.
    const hammered = applyHammerTriggers(this._state, this._hash, outcome.hammers);
    this._state = hammered.state;
    this._hash = hammered.hash;

    this._trace.push({ kind: 'dispatch', attackerId });

    // WIN is immediate and beats everything: the run ends the moment the board
    // empties, riders in flight or not.
    if (isWin(this._state)) {
      this._status = 'won';
      outcome.won = true;
    } else if (this.isDeadEnd()) {
      this._status = 'lost';
      outcome.lost = true;
    }
    return outcome;
  }

  /**
   * SETTLE — §2. "A lap completed with nothing else happening": arrive the
   * ring's oldest rider, then age the riders left behind (a settle is a player
   * action). A settle chosen while the front is at the age bound IS the forced
   * arrival — it discharges the obligation itself, so there is no age-bound
   * pre-step here and nothing ever arrives twice. Returns null when the ring
   * is empty.
   */
  settle(): ArrivalRecord | null {
    this._lastRefusal = null;
    if (this.over) { this._lastRefusal = 'game-over'; return null; }
    if (this._state.conveyor.length === 0) { this._lastRefusal = 'ring-empty'; return null; }

    const arrival = this.arriveOldest('settle');
    this.ageRing();
    this._trace.push({ kind: 'settle' });
    if (arrival !== null && arrival.overflow) this._status = 'lost';
    else if (this.isDeadEnd()) this._status = 'lost';
    return arrival;
  }

  /**
   * Quiescence drain — §2(c). When no dispatch is legal and the board is not
   * clear, the ring empties FIFO. Returns the arrivals it performed.
   */
  drain(): ArrivalRecord[] {
    const out: ArrivalRecord[] = [];
    while (!this.over && this._state.conveyor.length > 0) {
      const arrival = this.arriveOldest('drain');
      if (arrival === null) break;
      out.push(arrival);
      this._trace.push({ kind: 'settle' });
      if (arrival.overflow) { this._status = 'lost'; break; }
    }
    if (!this.over && this.isDeadEnd()) this._status = 'lost';
    return out;
  }

  /**
   * Assertion hook for the live game: the rider whose lap animation just ended
   * MUST be the ring's oldest, because riders launch and travel in dispatch
   * order. Anything else means the presentation layer has drifted from the
   * model, which is a hard error — see the assertion layer in the semantics doc.
   */
  peekOldestInFlight(): number | null {
    return this._state.conveyor.length > 0 ? this._state.conveyor[0].id : null;
  }

  /**
   * LIVE-mode invariant enforcement: ring accounting is owned by the LOGIC, and
   * the belt animation is pure display. Every ring rider must therefore have a
   * presentation entry that will eventually finish a lap and settle it. A ring
   * rider WITHOUT one (its lap animation was skipped, interrupted, or completed
   * out of order and already left the belt) can never settle on its own — left
   * alone it occupies belt slots forever while the belt renders empty, refusing
   * every dispatch: the 2026-09 fire-station soft-lock.
   *
   * Called by the scene once per frame with the set of attacker ids the belt is
   * actually presenting. Any FRONT rider missing from that set is settled here
   * — loudly, with a state dump — in ring (FIFO) order, exactly the arrival it
   * was always owed. Riders deeper in the ring are left alone until they reach
   * the front, which preserves model arrival order. Returns the arrivals
   * performed so the scene can present them (including a possible overflow
   * loss). Each is a real `settle`, so the event trace stays replayable.
   */
  reconcileWithPresentation(presentIds: ReadonlySet<number>): ArrivalRecord[] {
    const out: ArrivalRecord[] = [];
    while (!this.over) {
      const front = this._state.conveyor[0];
      if (front === undefined || presentIds.has(front.id)) break;
      console.error(
        `[ordered-flow] ring rider #${front.id} has no presentation entry — the model settles it ` +
        `now so its ${front.slotCount} belt slot(s) cannot leak. ` +
        describeFlowState(this._state),
      );
      const arrival = this.settle();
      if (arrival === null) break;
      out.push(arrival);
    }
    return out;
  }

  // ── Internals ────────────────────────────────────────────────────────────

  /** Move `conveyor[0]` into the stack. Null only when the ring is empty. */
  private arriveOldest(cause: ArrivalCause): ArrivalRecord | null {
    const rider = this._state.conveyor[0];
    if (rider === undefined) return null;

    // Exhausted riders never arrive anywhere. `dispatch` culls a depleted
    // attacker at resolve time and ring ammo never changes mid-ride, so an
    // all-empty rider here is unreachable through legal play — but if an
    // invariant upstream ever breaks, it must NOT be seated (a zero-ammo
    // attacker in the stack is undispatchable dead weight and reads as
    // corruption to the player). Cull it, loudly.
    if (rider.orbs.every(o => o.ammo <= 0)) {
      console.error(
        `[ordered-flow] ring rider #${rider.id} arrived with every orb empty — ` +
        `culling instead of seating (exhausted riders never arrive). ` +
        describeFlowState(this._state),
      );
      this._state = { ...this._state, conveyor: this._state.conveyor.slice(1) };
      this._hash = hashState(this._state);
      return { attackerId: rider.id, cause, stackSlot: -1, overflow: false, culled: true };
    }

    const used = stackSlotsUsed(this._state);
    const overflow = used + rider.slotCount > this._state.stackCap;

    const conveyor = this._state.conveyor.slice(1);
    // The ring-only `age` is stripped as the rider seats — the wait is over,
    // and the stack is a multiset in which ride time must not distinguish two
    // otherwise identical attackers. Mirrors `AtomicSimulator.applyArrival`.
    const settled: SolverAttacker = { ...rider };
    delete settled.age;
    // The overflowing arrival is still PLACED — the state carries
    // `stackSlotsUsed > stackCap`, which is what the loss condition reads. It is
    // not a refusal: declining to finish a lap is not a move the game offers.
    const stack = [...this._state.stack, settled];
    this._state = { ...this._state, conveyor, stack };
    this._hash = hashState(this._state);

    return { attackerId: rider.id, cause, stackSlot: used, overflow };
  }

  /** Locate a dispatchable attacker: a queue-lane front, or anything in the stack. */
  private locateDispatchable(
    attackerId: number,
  ): { attacker: SolverAttacker; source: 'queue' | 'stack'; homeLane: number } | null {
    for (let l = 0; l < this._state.queueLanes.length; l++) {
      const front = this._state.queueLanes[l][0];
      if (front && front.id === attackerId) return { attacker: front, source: 'queue', homeLane: l };
    }
    for (const a of this._state.stack) {
      if (a.id === attackerId) return { attacker: a, source: 'stack', homeLane: -1 };
    }
    return null;
  }

  /** Legality of a dispatch, as a refusal reason or null when legal. */
  private checkDispatch(attackerId: number): FlowRefusal | null {
    if (this.over) return 'game-over';
    const located = this.locateDispatchable(attackerId);
    if (located === null) return 'attacker-not-found';
    const { attacker, source, homeLane } = located;

    if (attacker.isHammer) return 'is-hammer';

    // Multi-orb consensus: the attacker must be at position 0 in every lane it
    // occupies. `packQueueLanes` guarantees those lanes are contiguous from its
    // home lane, so direct reference compares suffice.
    if (source === 'queue' && attacker.slotCount > 1) {
      for (let k = 1; k < attacker.slotCount; k++) {
        if (this._state.queueLanes[homeLane + k]?.[0] !== attacker) return 'no-consensus';
      }
    }

    if ((attacker.frozen ?? 0) > 0) {
      if (source !== 'queue') return 'frozen';
      if (!this.isStuckOnFrozenFront()) return 'frozen';
    }

    // Structural: an attacker wider than the whole belt can never ride it.
    if (attacker.slotCount > this._state.conveyorCap) return 'too-wide-for-belt';

    // Belt occupancy: dispatch is legal iff the ring has room RIGHT NOW. This
    // is the classic rule — a tap while the belt is full is refused (the UI
    // answers with the gentle conveyor flash), never a burst. Judged on the
    // state as it stands; a pending forced arrival does not pre-count.
    if (this.inFlightSlotsUsed() + attacker.slotCount > this._state.conveyorCap) {
      return 'no-belt-room';
    }

    return null;
  }

  /** Is the ring's front rider at the age bound (`age >= conveyorCap`)? */
  private frontAtAgeBound(): boolean {
    const front = this._state.conveyor[0];
    return front !== undefined && (front.age ?? 0) >= this._state.conveyorCap;
  }

  /**
   * A player action happened: every rider in the ring ages by 1, capped at
   * `conveyorCap` (the bound itself — beyond it nothing changes, and the cap
   * keeps the hashed domain finite; live mode can linger there since it never
   * enforces the bound). Fresh objects per aged rider keep `attackerHash`'s
   * identity-keyed memoisation sound. Mirrors `AtomicSimulator.ageConveyorWithHash`.
   */
  private ageRing(): void {
    if (this._state.conveyor.length === 0) return;
    const cap = this._state.conveyorCap;
    let changed = false;
    const conveyor = this._state.conveyor.map(a => {
      const oldAge = a.age ?? 0;
      const newAge = oldAge >= cap ? cap : oldAge + 1;
      if (newAge === oldAge) return a;
      changed = true;
      return { ...a, age: newAge };
    });
    if (!changed) return;
    this._state = { ...this._state, conveyor };
    this._hash = hashState(this._state);
  }

  /**
   * Stuck on a frozen front: nothing in flight, nothing in the stack, and no
   * non-frozen queue front dispatchable. Frozen counters only tick when an
   * attacker exhausts, so without this escape hatch the run would be dead.
   * Mirrors `QueueSystem.forceDispatchFrom` / `StrategicSimulator`'s fallback.
   */
  private isStuckOnFrozenFront(): boolean {
    if (this._state.conveyor.length > 0) return false;
    if (this._state.stack.length > 0) return false;
    const lanes = this._state.queueLanes;
    for (let l = 0; l < lanes.length; l++) {
      const front = lanes[l][0];
      if (!front) continue;
      if (front.isHammer) continue;
      if ((front.frozen ?? 0) > 0) continue;
      if (front.slotCount > 1) {
        let ok = true;
        for (let k = 1; k < front.slotCount; k++) {
          if (lanes[l + k]?.[0] !== front) { ok = false; break; }
        }
        if (!ok) continue;
      }
      if (front.slotCount > this._state.conveyorCap) continue;
      return false; // a normal move exists
    }
    for (const lane of lanes) {
      if (lane[0]) return true;
    }
    return false;
  }

  /** No dispatch legal, nothing in flight to settle, board not clear. */
  private isDeadEnd(): boolean {
    if (isWin(this._state)) return false;
    if (stackSlotsUsed(this._state) > this._state.stackCap) return true;
    if (this._state.conveyor.length > 0) return false; // drain is still available
    return this.legalDispatchIds().length === 0;
  }

  /** Remove an attacker from every queue lane holding it (always at position 0). */
  private removeFromQueue(attacker: SolverAttacker): void {
    const queueLanes = this._state.queueLanes.map(lane => {
      const idx = lane.indexOf(attacker);
      if (idx === -1) return lane;
      const next = lane.slice();
      next.splice(idx, 1);
      return next;
    });
    this._state = { ...this._state, queueLanes };
    this._hash = hashState(this._state);
  }

  private removeFromStack(attacker: SolverAttacker): void {
    const idx = this._state.stack.indexOf(attacker);
    if (idx === -1) return;
    const stack = this._state.stack.slice();
    stack.splice(idx, 1);
    this._state = { ...this._state, stack };
    this._hash = hashState(this._state);
  }

  /**
   * An attacker exhausted: every still-frozen QUEUE attacker's counter drops by
   * 1. Returns the ids that reached 0. Identity is preserved per attacker id so
   * a multi-orb spanning several lanes ticks once and keeps lane entries
   * reference-equal (consensus checks compare by reference).
   */
  private tickFrozenQueue(): number[] {
    const thawed: number[] = [];
    const idToNew = new Map<number, SolverAttacker>();
    let h = this._hash;
    const queueLanes = this._state.queueLanes.map((lane, laneIdx) =>
      lane.map((a, p) => {
        if ((a.frozen ?? 0) <= 0) return a;
        if (!idToNew.has(a.id)) {
          const newFrozen = (a.frozen ?? 0) - 1;
          const copy: SolverAttacker = { ...a };
          if (newFrozen <= 0) { delete copy.frozen; thawed.push(a.id); }
          else copy.frozen = newFrozen;
          idToNew.set(a.id, copy);
        }
        const copy = idToNew.get(a.id)!;
        h ^= getQueueLaneHash(laneIdx, p, attackerHash(a));
        h ^= getQueueLaneHash(laneIdx, p, attackerHash(copy));
        return copy;
      }),
    );
    if (idToNew.size === 0) return thawed;
    this._state = { ...this._state, queueLanes };
    this._hash = h;
    return thawed;
  }
}

/**
 * Canonical fingerprint of a flow state: the Zobrist hash plus the ring, which
 * `hashState` folds in positionally. Exposed for determinism tests that want a
 * value they can print when it mismatches.
 */
export function describeFlowState(state: SolverState): string {
  const lanes = state.queueLanes.map(l => l.map(a => a.id).join('.')).join('|');
  const ring = state.conveyor
    .map(a => `${a.id}@${a.age ?? 0}:${a.orbs.map(o => o.ammo).join(',')}`)
    .join(' ');
  const stack = canonicalizeStack(state.stack).map(a => `${a.id}:${a.orbs.map(o => o.ammo).join(',')}`).join(' ');
  let cubes = 0;
  for (let i = 0; i < state.board.length; i++) if (state.board[i] !== 0) cubes++;
  return `cubes=${cubes} lanes=[${lanes}] ring=[${ring}] stack=[${stack}]`;
}
